// src/lib/promo/promoUtils.js
// Pure helpers for Mudo promo tokens. No React, no DOM.

/**
 * @typedef {Object} PromoDefinition
 * @property {string} code
 * @property {number} discountPercent          // e.g. 10, 25, 50, 100
 * @property {boolean} isLifetime              // true for BLS-Free
 * @property {number|null} durationDays        // null for lifetime
 */

/**
 * @typedef {Object} PromoState
 * @property {string|null} code
 * @property {string|null} startedAt           // ISO string or null
 * @property {string|null} expiresAt           // ISO string or null
 * @property {number|null} discountPercent
 * @property {boolean} isLifetime
 */

/**
 * 1) Return known promo definitions map
 * @returns {Record<string, PromoDefinition>}
 */
export function getKnownPromoDefinitions() {
  return {
    "BLS-Free": { code: "BLS-Free", discountPercent: 100, isLifetime: true, durationDays: null },
    "MUDO10-1M": { code: "MUDO10-1M", discountPercent: 10, isLifetime: false, durationDays: 30 },
    "MUDO25-1M": { code: "MUDO25-1M", discountPercent: 25, isLifetime: false, durationDays: 30 },
    "MUDO50-1M": { code: "MUDO50-1M", discountPercent: 50, isLifetime: false, durationDays: 30 },
    "MUDOFREE-1M": { code: "MUDOFREE-1M", discountPercent: 100, isLifetime: false, durationDays: 30 },
    "MUDOFREE-3M": { code: "MUDOFREE-3M", discountPercent: 100, isLifetime: false, durationDays: 90 },
    "MUDOFREE-12M": { code: "MUDOFREE-12M", discountPercent: 100, isLifetime: false, durationDays: 365 },
  };
}

function toNumber(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function toDate(value) {
  const d = value instanceof Date ? value : new Date(value);
  return Number.isFinite(d.getTime()) ? d : new Date();
}

/**
 * 2) Parse a code into a PromoDefinition
 * @param {string} code
 * @returns {PromoDefinition|null}
 */
export function parsePromoCode(code) {
  if (!code || typeof code !== "string") return null;
  const raw = code.trim();
  if (!raw) return null;

  const known = getKnownPromoDefinitions();
  if (known[raw]) return known[raw];

  // Optional pattern parsing: MUDO<percent>-<n>M (percent off for n months)
  const m = /^MUDO(\d{1,2}|100)-(\d{1,2})M$/i.exec(raw);
  if (m) {
    const pct = clamp(toNumber(m[1], 0), 0, 100); // guard percent
    const n = Math.max(1, Number(m[2]));
    const days = n === 12 ? 365 : n * 30;
    return { code: raw, discountPercent: pct, isLifetime: false, durationDays: days };
  }
  return null;
}

/**
 * 3) Build a PromoState from a PromoDefinition
 * @param {PromoDefinition} def
 * @param {Date} [now]
 * @returns {PromoState}
 */
export function buildPromoStateFromDefinition(def, now = new Date()) {
  if (!def) {
    return { code: null, startedAt: null, expiresAt: null, discountPercent: null, isLifetime: false };
  }
  const safeNow = toDate(now); // guard now
  const startedAt = safeNow.toISOString();
  const expiresAt = def.isLifetime || def.durationDays == null
    ? null
    : new Date(safeNow.getTime() + def.durationDays * 86400000).toISOString();
  return {
    code: def.code || null,
    startedAt,
    expiresAt,
    discountPercent: clamp(toNumber(def.discountPercent, 0), 0, 100), // guard percent
    isLifetime: !!def.isLifetime,
  };
}

/**
 * 4) Determine if a promo is active at `now`
 * @param {PromoState|null} promo
 * @param {Date} [now]
 * @returns {boolean}
 */
export function isPromoActive(promo, now = new Date()) {
  if (!promo || !promo.code) return false;
  if (promo.isLifetime) return true;
  if (!promo.expiresAt) return false;
  const endMs = new Date(promo.expiresAt).getTime();
  if (!Number.isFinite(endMs)) return false;
  return toDate(now).getTime() <= endMs; // guard now
}

/**
 * 5) Remaining whole days (rounded up). Returns null when inactive or lifetime.
 * @param {PromoState|null} promo
 * @param {Date} [now]
 * @returns {number|null}
 */
export function getPromoDaysRemaining(promo, now = new Date()) {
  if (!promo || !isPromoActive(promo, now)) return null;
  if (promo.isLifetime) return null;
  if (!promo.expiresAt) return null;
  const endMs = new Date(promo.expiresAt).getTime();
  if (!Number.isFinite(endMs)) return null;
  const msDiff = endMs - toDate(now).getTime(); // guard now
  const days = Math.ceil(msDiff / 86400000);
  return days > 0 ? days : null;
}

/**
 * 6) Human-readable TopBar badge label
 * @param {PromoState|null} promo
 * @param {Date} [now]
 * @returns {string|null}
 */
export function formatPromoBadgeLabel(promo, now = new Date()) {
  if (!promo || !isPromoActive(promo, now)) return null;
  if (promo.isLifetime) {
    return promo.code === "BLS-Free"
      ? "BLS-Free · Lifetime access"
      : `${promo.code} · Lifetime access`;
  }
  const days = getPromoDaysRemaining(promo, now);
  if (days == null) return null;
  const pct = clamp(toNumber(promo.discountPercent, 0), 0, 100); // guard percent
  if (pct >= 100) return `Free · ${days} days left`;
  return `${pct}% off · ${days} days left`;
}

/**
 * 7) Derived status helper for components
 * @param {PromoState|null} promo
 * @param {Date} [now]
 * @returns {{isActive:boolean,isLifetime:boolean,daysRemaining:number|null,discountPercent:number|null,label:string|null}}
 */
export function derivePromoStatus(promo, now = new Date()) {
  const isActive = isPromoActive(promo, now);
  const isLifetime = !!promo?.isLifetime;
  const daysRemaining = getPromoDaysRemaining(promo, now);
  const discountPercent = clamp(toNumber(promo?.discountPercent, 0), 0, 100); // guard percent
  const label = formatPromoBadgeLabel(promo, now);
  return { isActive, isLifetime, daysRemaining, discountPercent, label };
}

// Named exports are declared above via `export function ...`.

/**
 * Back-compat: normalize arbitrary fields into a PromoState.
 * Recognizes snake_case fields from profile and camelCase.
 * If no explicit expiresAt and code matches a known def, compute expiry.
 * @param {Object|null} fields
 * @param {Date} [now]
 * @returns {PromoState}
 */
export function buildPromoState(fields, now = new Date()) {
  const f = fields || {};
  const code = f.promo_code ?? f.code ?? null;
  const isLifetime = !!(f.promo_is_lifetime ?? f.isLifetime);
  let discountPercent = f.promo_discount_percent ?? f.discountPercent ?? null;
  let startedAt = f.promo_started_at ?? f.startedAt ?? null;
  let expiresAt = f.promo_expires_at ?? f.expiresAt ?? null;
  const safeNow = toDate(now); // guard now

  if (!isLifetime && !expiresAt && code) {
    const def = parsePromoCode(code);
    if (def && def.durationDays != null) {
      startedAt = startedAt || safeNow.toISOString();
      expiresAt = new Date(safeNow.getTime() + def.durationDays * 86400000).toISOString();
    }
    if (discountPercent == null && def) {
      // Fill discountPercent from known definition if missing
      discountPercent = def.discountPercent;
    }
  }

  discountPercent = clamp(toNumber(discountPercent, 0), 0, 100); // guard percent
  return { code, startedAt, expiresAt, discountPercent, isLifetime };
}
